import * as m from "motion/react-m";
import type { FC } from "react";
import { useAiChatStore } from "@/domains/ai/ai-chat/stores/ai-chat.store";

/**
 * MessageSuggestions Component
 *
 * Displays AI-generated follow-up suggestions as clickable chips
 * Appears after assistant responses to help users quickly select next actions
 */
export const MessageSuggestions: FC = () => {
  const suggestions = useAiChatStore((state) => state.suggestions);

  const handleSuggestionClick = (suggestion: string) => {
    // Find the composer input element
    const input = document.querySelector<HTMLTextAreaElement>(".aui-composer-input");
    if (!input) {
      console.error("[MessageSuggestions] Could not find composer input element");
      return;
    }

    // Set the value using the native setter to bypass React's controlled input
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      "value"
    )?.set;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, suggestion);
    } else {
      input.value = suggestion;
    }

    // Trigger both input and change events to ensure React detects the change
    const inputEvent = new Event("input", { bubbles: true });
    const changeEvent = new Event("change", { bubbles: true });
    input.dispatchEvent(inputEvent);
    input.dispatchEvent(changeEvent);

    // Focus the input so user can review and send
    input.focus();
  };

  // Don't render if no suggestions
  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="aui-suggestions-wrapper flex gap-2 overflow-x-auto pb-3 px-1 scrollbar-hide">
      {suggestions.map((suggestion, index) => (
        <m.button
          key={suggestion}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => handleSuggestionClick(suggestion)}
          className="aui-suggestion-chip rounded-full border border-border bg-background px-4 py-2 text-sm hover:bg-accent whitespace-nowrap transition-colors cursor-pointer flex-shrink-0"
          aria-label={`Suggestion: ${suggestion}`}
        >
          {suggestion}
        </m.button>
      ))}
    </div>
  );
};
